/**
 * ClickablePhoneNumber — 클릭 시 클립보드 복사 + toast 안내
 *
 * 전화번호가 노출되는 모든 위치에서 일관된 UX 제공:
 * - 표시: phoneFormatter로 010-1234-5678 형식
 * - 클릭: 표시된 형식 그대로 클립보드 복사 (사용자가 dialer/메모에 붙여넣기 친화)
 * - 안내: sonner toast.success ("전화번호가 복사되었습니다")
 *
 * 사용 예:
 *   <ClickablePhoneNumber phone={consult.customerPhone} />
 *   <ClickablePhoneNumber phone={appointment.phone} className="text-sm" />
 *
 * phone이 null/빈 값이면 "-" 표시 + 클릭 비활성.
 */

import { Phone } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { formatPhoneNumber } from "@/utils/phoneFormatter";
import { HighlightText } from "@/components/common/HighlightText";

interface ClickablePhoneNumberProps {
  phone: string | null | undefined;
  className?: string;
  /** 아이콘 표시 여부 (기본 true) */
  showIcon?: boolean;
  /** 표시 형식 — formatted: 010-1234-5678, raw: 입력 그대로 (기본 formatted) */
  display?: "formatted" | "raw";
  /** 검색어 highlight (있으면 표시 텍스트에서 매칭 부분 강조) */
  highlight?: string;
  /** hover tooltip 텍스트 (기본 "클릭하여 복사") */
  title?: string;
}

export default function ClickablePhoneNumber({
  phone,
  className,
  showIcon = true,
  display = "formatted",
  highlight,
  title = "클릭하여 복사",
}: ClickablePhoneNumberProps) {
  const isEmpty = !phone || phone.trim() === "";
  const displayText = isEmpty
    ? "-"
    : display === "formatted"
      ? formatPhoneNumber(phone)
      : phone;

  const handleClick = async (e: React.MouseEvent) => {
    if (isEmpty) return;
    e.stopPropagation(); // 부모 row click 등 트리거 방지

    try {
      await navigator.clipboard.writeText(displayText);
      toast.success("전화번호가 복사되었습니다", {
        description: displayText,
        duration: 2000,
      });
    } catch (err) {
      toast.error("복사에 실패했습니다. 브라우저 권한을 확인해주세요.");
    }
  };

  if (isEmpty) {
    return <span className={cn("text-muted-foreground", className)}>-</span>;
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        "inline-flex items-center gap-1 hover:underline hover:text-primary transition-colors cursor-pointer",
        className,
      )}
      title={title}
      aria-label={`${displayText} 복사`}
    >
      {showIcon && <Phone className="h-3 w-3 shrink-0" />}
      {highlight ? (
        <HighlightText text={displayText} highlight={highlight} />
      ) : (
        <span>{displayText}</span>
      )}
    </button>
  );
}
